import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SuperAdminLayout from "../components/SuperAdminLayout";
import api from "../services/api";


const STATUS_FILTERS = ["all", "active", "trial", "expired", "suspended"];


const PLANS = [
  { value: "starter",    label: "Starter",    price: "KES 999" },
  { value: "growth",     label: "Growth",     price: "KES 2,499" },
  { value: "enterprise", label: "Enterprise", price: "KES 5,999" },
];


export default function SuperAdminShopsPage() {
  const [shops, setShops]       = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [statusFilter, setStatus] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast]       = useState(null);

  const navigate = useNavigate();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchShops(); }, []);


  useEffect(() => {
    let result = shops;
    if (statusFilter !== "all") {
      result = result.filter((s) => s.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.phone?.toLowerCase().includes(q) ||
          s.owner?.name?.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [shops, search, statusFilter]);


  const fetchShops = async () => {
    setLoading(true);
    try {
      const res = await api.get("/super-admin/shops");
      setShops(res.data);
    } catch {
      showToast("Failed to load shops.", "error");
    } finally {
      setLoading(false);
    }
  };


  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };


  const handleToggle = async (shop) => {
    setActionLoading(`toggle-${shop.id}`);
    try {
      const res = await api.patch(`/super-admin/shops/${shop.id}/toggle`);
      setShops((prev) =>
        prev.map((s) =>
          s.id === shop.id
            ? { ...s, is_active: res.data.is_active, status: res.data.is_active ? "active" : "suspended" }
            : s
        )
      );
      showToast(res.data.message);
    } catch {
      showToast("Action failed. Try again.", "error");
    } finally {
      setActionLoading(null);
    }
  };


  const handleExtendTrial = async (shop) => {
    setActionLoading(`trial-${shop.id}`);
    try {
      const res = await api.patch(`/super-admin/shops/${shop.id}/extend-trial`);
      setShops((prev) =>
        prev.map((s) =>
          s.id === shop.id
            ? { ...s, trial_ends_at: res.data.trial_ends_at, status: "trial", is_active: true }
            : s
        )
      );
      showToast("Trial extended by 7 days.");
    } catch {
      showToast("Failed to extend trial.", "error");
    } finally {
      setActionLoading(null);
    }
  };


  const handlePlanChange = async (shop, newPlan) => {
    if (newPlan === shop.plan.toLowerCase()) return;
    setActionLoading(`plan-${shop.id}`);
    try {
      const res = await api.patch(`/super-admin/shops/${shop.id}/plan`, { plan: newPlan });
      setShops((prev) =>
        prev.map((s) =>
          s.id === shop.id
            ? { ...s, plan: res.data.plan.charAt(0).toUpperCase() + res.data.plan.slice(1), status: "active", trial_ends_at: null }
            : s
        )
      );
      showToast(`Plan updated to ${res.data.plan}.`);
    } catch {
      showToast("Failed to update plan.", "error");
    } finally {
      setActionLoading(null);
    }
  };


  const openWhatsApp = (phone) => {
    const clean = phone?.replace(/^0/, "254");
    window.open(`https://wa.me/${clean}`, "_blank");
  };


  const statusBadge = (status) => {
    const map = {
      active:    "bg-green-500/10 text-green-400 border border-green-500/20",
      trial:     "bg-amber-500/10 text-amber-400 border border-amber-500/20",
      expired:   "bg-red-500/10 text-red-400 border border-red-500/20",
      suspended: "bg-slate-500/10 text-slate-400 border border-slate-500/20",
    };
    return map[status] || map.suspended;
  };


  const planColor = (plan) => {
    const map = {
      Starter:    "text-blue-400",
      Growth:     "text-purple-400",
      Enterprise: "text-orange-400",
    };
    return map[plan] || "text-slate-400";
  };


  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" })
      : "—";


  const counts = {
    all:       shops.length,
    active:    shops.filter((s) => s.status === "active").length,
    trial:     shops.filter((s) => s.status === "trial").length,
    expired:   shops.filter((s) => s.status === "expired").length,
    suspended: shops.filter((s) => s.status === "suspended").length,
  };


  const Spinner = () => (
    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
    </svg>
  );


  return (
    <SuperAdminLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>


      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-sm font-medium ${
          toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
        }`}>
          {toast.type === "error" ? "❌" : "✅"} {toast.message}
        </div>
      )}


      <div className="p-7 space-y-6">


        {/* ── HEADER ── */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>
              All Shops
            </h1>
            <p className="text-slate-400 text-sm mt-1">{shops.length} shops on the platform.</p>
          </div>
          <button
            onClick={fetchShops}
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors border border-slate-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            Refresh
          </button>
        </div>


        {/* ── SEARCH + FILTER ── */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 15.803a7.5 7.5 0 0 0 10.607 0z" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by shop name, phone or owner..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-xl p-1">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
                  statusFilter === s ? "bg-orange-500 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {s} {counts[s] > 0 && <span className="opacity-70">({counts[s]})</span>}
              </button>
            ))}
          </div>
        </div>


        {/* ── TABLE ── */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400 gap-3">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              Loading shops...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-500">
              <p className="text-3xl mb-3">🏪</p>
              <p className="font-medium">No shops found.</p>
              <p className="text-sm mt-1">Try adjusting your search or filter.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800">
                    {["Shop", "Owner", "Status", "Plan", "Trial Ends", "Drivers", "Orders", "Contact", "Actions"].map((h) => (
                      <th key={h} className="text-left px-4 py-3.5 text-slate-400 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {filtered.map((shop) => (
                    <tr key={shop.id} className="hover:bg-slate-800/40 transition-colors">


                      {/* Shop */}
                      <td className="px-4 py-4">
                        <p className="text-white font-semibold">{shop.name}</p>
                        <p className="text-slate-500 text-xs">{shop.phone}</p>
                      </td>


                      {/* Owner */}
                      <td className="px-4 py-4">
                        <p className="text-slate-300 text-sm">{shop.owner?.name || "—"}</p>
                        <p className="text-slate-500 text-xs">{shop.owner?.phone || ""}</p>
                      </td>


                      {/* Status */}
                      <td className="px-4 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusBadge(shop.status)}`}>
                          {shop.status}
                        </span>
                      </td>


                      {/* Plan Dropdown */}
                      <td className="px-4 py-4">
                        <div className="relative">
                          <select
                            value={shop.plan?.toLowerCase() || "starter"}
                            onChange={(e) => handlePlanChange(shop, e.target.value)}
                            disabled={!!actionLoading}
                            className={`appearance-none bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs font-semibold pr-7 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors cursor-pointer disabled:opacity-50 ${planColor(shop.plan)}`}
                          >
                            {PLANS.map((p) => (
                              <option key={p.value} value={p.value} className="text-white bg-slate-800">
                                {p.label} — {p.price}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
                            {actionLoading === `plan-${shop.id}` ? (
                              <Spinner />
                            ) : (
                              <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </td>


                      {/* Trial Ends */}
                      <td className="px-4 py-4 text-slate-400 text-xs whitespace-nowrap">
                        {formatDate(shop.trial_ends_at)}
                      </td>


                      {/* Drivers */}
                      <td className="px-4 py-4 text-slate-300 text-sm">{shop.driver_count}</td>


                      {/* Orders */}
                      <td className="px-4 py-4 text-slate-300 text-sm">{shop.orders_count}</td>


                      {/* Contact */}
                      <td className="px-4 py-4">
                        <button
                          onClick={() => openWhatsApp(shop.owner?.phone)}
                          className="bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/20 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors"
                        >
                          💬 WA
                        </button>
                      </td>


                      {/* Actions */}
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/super-admin/shops/${shop.id}`)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20 transition-all whitespace-nowrap"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleToggle(shop)}
                            disabled={!!actionLoading}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 whitespace-nowrap ${
                              shop.is_active
                                ? "bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
                                : "bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20"
                            }`}
                          >
                            {actionLoading === `toggle-${shop.id}` ? <Spinner /> : shop.is_active ? "Suspend" : "Activate"}
                          </button>
                          <button
                            onClick={() => handleExtendTrial(shop)}
                            disabled={!!actionLoading}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 transition-all disabled:opacity-50 whitespace-nowrap"
                          >
                            {actionLoading === `trial-${shop.id}` ? <Spinner /> : "+7 Days"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>


        {!loading && filtered.length > 0 && (
          <p className="text-slate-500 text-xs text-right">
            Showing {filtered.length} of {shops.length} shops
          </p>
        )}
      </div>
    </SuperAdminLayout>
  );
}